/**
 * Sanitize text to prevent XSS attacks
 * 
 * This function escapes HTML special characters to prevent script injection
 * when displaying user-generated or backend-provided content.
 * 
 * @param {string} text - The text to sanitize
 * @returns {string} - The sanitized text with HTML entities escaped
 */
export function sanitizeText(text) {
    if (typeof text !== 'string') {
        return '';
    }

    // Create a temporary element to leverage browser's built-in HTML escaping
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Capitalize the first letter of a string
 *
 * @param {string} str - The string to capitalize
 * @returns {string} - The string with first letter capitalized
 */
function capitalizeFirstLetter(str) {
    if (!str || typeof str !== 'string') {
        return '';
    }

    // Trim leading whitespace to find the first actual character
    const trimmed = str.trimStart();
    if (trimmed.length === 0) {
        return str; // Return original if only whitespace
    }

    // Find the first letter (skip special characters and numbers)
    let firstLetterIndex = -1;
    for (let i = 0; i < trimmed.length; i++) {
        if (/[a-zA-Z]/.test(trimmed[i])) {
            firstLetterIndex = i;
            break;
        }
    }

    // If no letter found, return as-is
    if (firstLetterIndex === -1) {
        return str;
    }

    // Calculate the actual index in the original string (accounting for leading whitespace)
    const leadingWhitespace = str.length - trimmed.length;
    const actualIndex = leadingWhitespace + firstLetterIndex;

    // Capitalize the first letter
    return str.substring(0, actualIndex) +
           str[actualIndex].toUpperCase() +
           str.substring(actualIndex + 1);
}

/**
 * Sanitize error message from backend API
 *
 * This function sanitizes error messages to prevent XSS while preserving
 * basic formatting like line breaks and ensuring proper capitalization.
 *
 * @param {string} message - The error message to sanitize
 * @returns {string} - The sanitized and formatted error message
 */
export function sanitizeErrorMessage(message) {
    if (!message || typeof message !== 'string') {
        return '';
    }

    // First, capitalize the first letter of the message
    const capitalized = capitalizeFirstLetter(message);

    // Then, escape all HTML special characters
    const sanitized = sanitizeText(capitalized);

    // Optionally preserve line breaks by converting \n to <br>
    // This is safe because we've already escaped all HTML
    return sanitized.replace(/\n/g, '<br>');
}

export default sanitizeText;

